import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectSelectedInstrument } from 'store/entities/market/selectors';
import { orderbookSelector } from 'store/entities/market/orderbook/selectors';

import { getLimitPriceValidationMessages } from 'helpers';

import { OrderSide, OrderType } from 'types';
import { useAccount } from 'wagmi';

export const useLimitPriceValidation = (
  price: string,
  tradeType: OrderType,
  tradeSide: OrderSide,
  focusInput: boolean
): string => {
  const { address } = useAccount();
  const [error, setError] = useState('');

  const validationMessages = getLimitPriceValidationMessages(_t);

  const orderbook = useSelector(orderbookSelector);
  const instrument = useSelector(selectSelectedInstrument);

  useEffect(() => {
    if (tradeType === OrderType.Market) {
      setError(validationMessages.default);
      return;
    }
    if (orderbook?.sell?.length && !price) return;

    if (!address && focusInput) {
      setError(validationMessages.walletNotConnected);
      return;
    }

    const limitPrice = Number(price);

    const sellMaxPrice = 1.5 * ((orderbook?.sell[0]?.price + orderbook?.sell[orderbook?.sell.length - 1]?.price) / 2);
    const sellMinPrice = orderbook?.buy[0]?.price;

    const buyMinPrice = 0.5 * ((orderbook?.buy[0]?.price + orderbook?.buy[orderbook?.buy.length - 1]?.price) / 2);
    const buyMaxPrice = orderbook?.sell[0]?.price;

    if (tradeSide === OrderSide.Sell) {
      if (limitPrice > Number(sellMaxPrice)) {
        setError(`${validationMessages.maxPrice} ${sellMaxPrice}`);
        return;
      }
      if (limitPrice < Number(sellMinPrice)) {
        setError(`${validationMessages.minPrice} ${sellMinPrice}`);
        return;
      }
    }
    if (tradeSide === OrderSide.Buy) {
      if (limitPrice < Number(buyMinPrice)) {
        setError(`${validationMessages.minPrice} ${buyMinPrice}`);
        return;
      }
      if (limitPrice > Number(buyMaxPrice)) {
        setError(`${validationMessages.maxPrice} ${buyMaxPrice}`);
        return;
      }
    }

    setError(validationMessages.default);
  }, [price, orderbook, instrument, focusInput]);

  return error;
};
